import { useState } from 'react';
import { usePEOAssignments } from '@/hooks/use-assignment-management';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Clock, 
  Calendar, 
  MapPin, 
  Phone, 
  Mail, 
  Building2, 
  Users, 
  DollarSign,
  Eye,
  CheckCircle,
  XCircle,
  PlayCircle,
  AlertTriangle,
  FileText,
  Briefcase
} from 'lucide-react';
import { format } from 'date-fns';
import { ApplicationAssignment, UpdateAssignmentRequest } from '@/types/assignment.types';
import { ApplicationContextPanel } from './ApplicationContextPanel';

interface AssignmentInboxProps {
  providerId: string;
}

export const AssignmentInbox = ({ providerId }: AssignmentInboxProps) => {
  console.log('AssignmentInbox: Rendered with providerId:', providerId);
  
  const { assignments, isLoading, updateAssignment, error } = usePEOAssignments(providerId);
  const [selectedAssignment, setSelectedAssignment] = useState<ApplicationAssignment | null>(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [statusUpdate, setStatusUpdate] = useState('');
  const [notes, setNotes] = useState('');

  console.log('AssignmentInbox: State:', { 
    assignmentsCount: assignments?.length || 0, 
    isLoading, 
    hasError: !!error,
    providerId 
  });

  const handleStatusUpdate = (assignmentId: string, newStatus: string) => {
    const updates: UpdateAssignmentRequest = {
      assignment_status: newStatus as any,
    };

    if (notes) {
      updates.internal_notes = notes;
    }

    updateAssignment.mutate({ id: assignmentId, updates });
    setStatusUpdate('');
    setNotes('');
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'bg-red-100 text-red-800 border-red-200';
      case 'high': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'normal': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'low': return 'bg-gray-100 text-gray-800 border-gray-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending': return <Clock className="h-4 w-4" />;
      case 'in_progress': return <PlayCircle className="h-4 w-4" />;
      case 'completed': return <CheckCircle className="h-4 w-4" />;
      case 'declined': return <XCircle className="h-4 w-4" />;
      default: return <AlertTriangle className="h-4 w-4" />;
    }
  };

  const filterAssignmentsByStatus = (status: string) => {
    return assignments.filter(assignment => assignment.assignment_status === status);
  };

  // Show error state if there's an error
  if (error) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center min-h-[300px] py-8">
          <AlertTriangle className="h-12 w-12 mb-4 text-destructive" />
          <h3 className="text-lg font-medium mb-1">Error Loading Assignments</h3>
          <p className="text-muted-foreground text-center mb-4">
            There was an error loading your assignments. Please try refreshing the page.
          </p>
          <Button variant="outline" onClick={() => window.location.reload()}>
            Refresh Page
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Show loading state
  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex justify-center items-center min-h-[300px] py-8">
          <div className="flex flex-col items-center gap-4">
            <div className="w-8 h-8 border-t-2 border-primary rounded-full animate-spin"></div>
            <p className="text-sm text-muted-foreground">Loading assignments...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Show empty state if no assignments
  if (!assignments || assignments.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center min-h-[300px] py-8">
          <FileText className="h-12 w-12 mb-4 text-muted-foreground" />
          <h3 className="text-lg font-medium mb-1">No assignments yet</h3>
          <p className="text-muted-foreground text-center">
            When administrators assign applications to your organization, they'll appear here.
          </p>
        </CardContent>
      </Card>
    );
  }

  const pendingCount = filterAssignmentsByStatus('pending').length;
  const inProgressCount = filterAssignmentsByStatus('in_progress').length;
  const completedCount = filterAssignmentsByStatus('completed').length;

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-2xl font-bold">Assignment Inbox</h3>
        <p className="text-muted-foreground">Manage your assigned applications and create quotes</p>
      </div>

      <Tabs defaultValue="pending" className="space-y-4">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="all">
            All ({assignments.length})
          </TabsTrigger>
          <TabsTrigger value="pending">
            Pending ({pendingCount})
          </TabsTrigger>
          <TabsTrigger value="in_progress">
            In Progress ({inProgressCount})
          </TabsTrigger>
          <TabsTrigger value="completed">
            Completed ({completedCount})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="all">
          <AssignmentList 
            assignments={assignments} 
            onViewDetails={(assignment) => {
              setSelectedAssignment(assignment);
              setIsDetailsOpen(true);
            }}
            onStatusUpdate={handleStatusUpdate}
            getPriorityColor={getPriorityColor}
            getStatusIcon={getStatusIcon}
          />
        </TabsContent>

        <TabsContent value="pending">
          <AssignmentList 
            assignments={filterAssignmentsByStatus('pending')} 
            onViewDetails={(assignment) => {
              setSelectedAssignment(assignment);
              setIsDetailsOpen(true);
            }}
            onStatusUpdate={handleStatusUpdate}
            getPriorityColor={getPriorityColor}
            getStatusIcon={getStatusIcon}
          />
        </TabsContent>

        <TabsContent value="in_progress">
          <AssignmentList 
            assignments={filterAssignmentsByStatus('in_progress')} 
            onViewDetails={(assignment) => {
              setSelectedAssignment(assignment);
              setIsDetailsOpen(true);
            }}
            onStatusUpdate={handleStatusUpdate}
            getPriorityColor={getPriorityColor}
            getStatusIcon={getStatusIcon}
          />
        </TabsContent>

        <TabsContent value="completed">
          <AssignmentList 
            assignments={filterAssignmentsByStatus('completed')} 
            onViewDetails={(assignment) => {
              setSelectedAssignment(assignment);
              setIsDetailsOpen(true);
            }}
            onStatusUpdate={handleStatusUpdate}
            getPriorityColor={getPriorityColor}
            getStatusIcon={getStatusIcon}
          />
        </TabsContent>
      </Tabs>

      {/* Assignment Details Dialog */}
      <Dialog open={isDetailsOpen} onOpenChange={setIsDetailsOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Assignment Details</DialogTitle>
          </DialogHeader>
          
          {selectedAssignment && (
            <div className="space-y-6">
              <div className="flex items-start justify-between">
                <div>
                  <h4 className="text-lg font-semibold">
                    {selectedAssignment.application?.company_legal_name || 
                     `${selectedAssignment.application?.first_name} ${selectedAssignment.application?.last_name}`}
                  </h4>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge className={getPriorityColor(selectedAssignment.priority)}>
                      {selectedAssignment.priority} Priority
                    </Badge>
                    <Badge variant="outline">
                      {getStatusIcon(selectedAssignment.assignment_status)}
                      <span className="ml-1">{selectedAssignment.assignment_status.replace('_', ' ')}</span>
                    </Badge>
                  </div>
                </div>
                
                <div className="flex gap-2">
                  <Select
                    value={statusUpdate || selectedAssignment.assignment_status}
                    onValueChange={setStatusUpdate}
                  >
                    <SelectTrigger className="w-40">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="declined">Declined</SelectItem>
                    </SelectContent>
                  </Select>
                  
                  {statusUpdate && statusUpdate !== selectedAssignment.assignment_status && (
                    <Button
                      onClick={() => handleStatusUpdate(selectedAssignment.id, statusUpdate)}
                      disabled={updateAssignment.isPending}
                    >
                      Update
                    </Button>
                  )}
                </div>
              </div>

              {selectedAssignment.assignment_notes && (
                <div className="p-4 bg-blue-50 rounded-lg">
                  <h5 className="font-medium text-blue-900 mb-2">Assignment Notes</h5>
                  <p className="text-blue-800">{selectedAssignment.assignment_notes}</p>
                </div>
              )}

              <Separator />

              <ApplicationContextPanel application={selectedAssignment.application!} />

              <Separator />

              <div>
                <h5 className="font-medium mb-2">Add Notes</h5>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Add notes about this assignment..."
                />
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

interface AssignmentListProps {
  assignments: ApplicationAssignment[];
  onViewDetails: (assignment: ApplicationAssignment) => void;
  onStatusUpdate: (assignmentId: string, status: string) => void;
  getPriorityColor: (priority: string) => string;
  getStatusIcon: (status: string) => React.ReactNode;
}

const AssignmentList = ({ 
  assignments, 
  onViewDetails, 
  onStatusUpdate,
  getPriorityColor,
  getStatusIcon
}: AssignmentListProps) => {
  if (assignments.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center min-h-[200px] py-8">
          <FileText className="h-12 w-12 mb-4 text-muted-foreground" />
          <h4 className="text-lg font-medium mb-1">No assignments in this category</h4>
          <p className="text-muted-foreground">Assignments will appear here as they're created.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid gap-4">
      {assignments.map((assignment) => (
        <Card key={assignment.id} className="hover:shadow-md transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-start gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h4 className="font-semibold">
                        {assignment.application?.company_legal_name || 
                         `${assignment.application?.first_name} ${assignment.application?.last_name}`}
                      </h4>
                      <Badge className={getPriorityColor(assignment.priority)} variant="secondary">
                        {assignment.priority}
                      </Badge>
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-muted-foreground mb-4">
                      <div className="flex items-center gap-1">
                        <Users className="h-4 w-4" />
                        {assignment.application?.w2_employee_count} employees
                      </div>
                      
                      <div className="flex items-center gap-1">
                        <Briefcase className="h-4 w-4" />
                        {assignment.application?.primary_industry}
                      </div>
                      
                      <div className="flex items-center gap-1">
                        <MapPin className="h-4 w-4" />
                        {assignment.application?.city}, {assignment.application?.state}
                      </div>
                      
                      <div className="flex items-center gap-1">
                        <Calendar className="h-4 w-4" />
                        {format(new Date(assignment.created_at), 'MMM d, yyyy')}
                      </div>
                    </div>

                    {assignment.due_date && (
                      <div className="flex items-center gap-1 text-sm text-orange-600 mb-2">
                        <Clock className="h-4 w-4" />
                        Due: {format(new Date(assignment.due_date), 'MMM d, h:mm a')}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="flex items-center gap-1">
                  {getStatusIcon(assignment.assignment_status)}
                  {assignment.assignment_status.replace('_', ' ')}
                </Badge>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onViewDetails(assignment)}
                >
                  <Eye className="h-4 w-4 mr-1" />
                  View Details
                </Button>

                {assignment.assignment_status === 'pending' && (
                  <Button
                    size="sm"
                    onClick={() => onStatusUpdate(assignment.id, 'in_progress')}
                  >
                    Start Work
                  </Button>
                )}

                {assignment.assignment_status === 'in_progress' && (
                  <Button
                    size="sm"
                    onClick={() => onStatusUpdate(assignment.id, 'completed')}
                  >
                    Mark Complete
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};